import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Stack from '@mui/material/Stack';

import sortFields from 'api/sortFields';
import {
  useUpdateEffect,
  useMountEffect,
  useTableState,
  useFilterState,
} from 'shared/hooks';
import { adminActions } from 'features/admin/store/slice';
import { getTableHeaders, prepareTableData } from 'utils/tables';
import { formatDateToStr } from 'utils/dates';

import AddButton from 'navigation/components/AddButton';
import PaginatedTable from 'shared/components/data/PaginatedTable/PaginatedTable';
import PageTitle from 'shared/components/layout/PageTitle/PageTitle';
import TableResultCount from 'shared/components/data/Table/TableResultCount';
import SearchField from 'shared/components/forms/SearchField/SearchField';
import {
  FlexBoxColumn,
  FlexBoxSpaceBetween,
  FlexBoxSpaceBetweenCenter,
} from 'styles/layout';

const tableColumns = [
  {
    header: {
      id: sortFields.hl7Patients.familyName,
      value: 'Family Name',
      isSortable: true,
    },
    dataKey: 'familyName',
    width: '15%',
  },
  {
    header: {
      id: sortFields.hl7Patients.givenName,
      value: 'Given Name',
      isSortable: true,
    },
    dataKey: 'givenName',
    width: '15%',
  },
  {
    header: {
      id: sortFields.hl7Patients.dob,
      value: 'Date of Birth',
      isSortable: true,
    },
    formatValue: ({ dob }) => (dob ? formatDateToStr(dob) : '-'),
    width: '12%',
  },
  {
    header: {
      id: sortFields.hl7Patients.mrn,
      value: 'MRN',
      isSortable: true,
    },
    dataKey: 'mrn',
    width: '12%',
  },
  {
    header: {
      id: sortFields.hl7Patients.siteCode,
      value: 'Site Code',
      isSortable: true,
    },
    dataKey: 'siteCode',
    width: '12%',
  },
  {
    header: {
      id: sortFields.hl7Patients.sendingFacility,
      value: 'Sending Facility',
      isSortable: true,
    },
    dataKey: 'sendingFacility',
  },
];

const Hl7PatientList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: patients, pageCount, count } = useSelector(
    ({ admin }) => admin.hl7Patients,
  );

  const {
    page,
    sortBy,
    sortOrder,
    handlePageChange,
    handleSortChange,
    setPage,
    setQueryParams,
  } = useTableState(sortFields.hl7Patients.familyName);

  const { filters, handleFilter } = useFilterState({ query: '' }, setPage);

  const fetchList = React.useCallback(() => {
    dispatch(
      adminActions.listHl7Patients({ page, sortBy, sortOrder, filters }),
    );
    setQueryParams({
      ...filters,
      page,
      sortBy,
      sortOrder,
    });
  }, [dispatch, page, sortBy, sortOrder, filters, setQueryParams]);

  useMountEffect(fetchList);

  useUpdateEffect(() => {
    fetchList();
  }, [page, sortBy, sortOrder, filters]);

  return (
    <FlexBoxColumn>
      <FlexBoxSpaceBetweenCenter>
        <PageTitle>HL7 Patients</PageTitle>
        <Stack
          spacing={1}
          direction="row"
          useFlexGap
          flexWrap="wrap"
          alignItems="center"
          justifyContent="flex-end"
        >
          <AddButton to="add">Add Patient</AddButton>
          <TableResultCount totalCount={count} />
        </Stack>
      </FlexBoxSpaceBetweenCenter>

      <FlexBoxSpaceBetween mb={2}>
        <SearchField
          onChange={(value) => handleFilter({ query: value })}
          value={filters.query}
        />
      </FlexBoxSpaceBetween>

      <FlexBoxColumn>
        <PaginatedTable
          headings={getTableHeaders(tableColumns)}
          data={prepareTableData(patients, tableColumns)}
          fallbackMsg="No HL7 patients found"
          sortBy={sortBy}
          sortOrder={sortOrder}
          currentPage={page}
          totalPages={pageCount}
          onPageChange={handlePageChange}
          onSortChange={handleSortChange}
          onClickRow={(id) => navigate(`${id}`)}
        />
      </FlexBoxColumn>
    </FlexBoxColumn>
  );
};

export default Hl7PatientList;
